/* @generated */

// ../src/overlay/overlay.tsx
import classNames from "classnames";
import * as React from "react";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import {
  useFloating,
  autoUpdate,
  flip,
  shift,
  limitShift,
  offset,
  size,
  arrow,
  hide
} from "@floating-ui/react";
import { ELEVATED_BASE_INDEX, Layer } from "@dropbox/dig-components/layer";

// ../src/_global/roundByDPR.ts
function roundByDPR(value) {
  const dpr = typeof window !== "undefined" && typeof (window == null ? void 0 : window.devicePixelRatio) === "number" ? window.devicePixelRatio : 1;
  return Math.round(value * dpr) / dpr;
}

// ../src/overlay/overlay.tsx
var Overlay = React.forwardRef(
  ({
    className,
    children,
    placement: _initialPlacement,
    boundaryRef,
    anchorRef,
    auto = false,
    shouldHideOnAnchorHidden = false,
    shouldHideOnOverlayEscape = false,
    isVisibleOnScroll = false,
    onChangePlacement,
    offsetDistance = 0,
    isPortaled = true,
    positioningStrategy = "absolute",
    style,
    setWidthSameAsAnchor = false,
    arrowRef,
    shiftBoundaryDirection = { horizontal: true, vertical: true },
    _arrowPadding = 6,
    elementResize = false,
    ...rest
  }, ref) => {
    const [shouldReAttachAnchorRef, setShouldReAttachAnchorRef] = React.useState(false);
    const updateAdjustedPlacement = {
      name: "updateAdjustedPlacement",
      fn({ placement: newPlacement }) {
        if (onChangePlacement) {
          onChangePlacement(newPlacement);
        }
        return {};
      }
    };
    const applyFn = ({ elements, rects }) => {
      Object.assign(elements.floating.style, {
        width: `${rects.reference.width}px`
      });
    };
    const {
      x,
      y,
      refs,
      strategy,
      update,
      middlewareData,
      placement: _floatingPlacement
    } = useFloating({
      placement: _initialPlacement,
      strategy: positioningStrategy,
      whileElementsMounted: (reference, floating, update2) => autoUpdate(reference, floating, update2, {
        // If false, this causes Floating UI to *not* use any ResizeObservers.
        // @see https://floating-ui.com/docs/autoupdate#elementresize
        elementResize
      }),
      middleware: [
        ...isVisibleOnScroll && !auto ? [
          shift({
            boundary: boundaryRef && boundaryRef.current ? boundaryRef.current : void 0,
            crossAxis: true
          })
        ] : [],
        // Offset needs to come after `shift` or else the offset is lost.
        offset(offsetDistance),
        ...setWidthSameAsAnchor ? [
          size({
            apply: applyFn
          })
        ] : [],
        ...auto ? [
          flip({
            boundary: boundaryRef && boundaryRef.current ? boundaryRef.current : void 0,
            fallbackStrategy: "initialPlacement"
          })
        ] : [],
        ...auto ? [
          boundaryRef && boundaryRef.current ? shift({
            crossAxis: shiftBoundaryDirection.vertical,
            mainAxis: shiftBoundaryDirection.horizontal,
            boundary: boundaryRef.current
          }) : shift({ limiter: limitShift() })
        ] : [],
        // Make sure the arrow has some space apart from the edge of the floating element.
        ...arrowRef ? [arrow({ element: arrowRef, padding: _arrowPadding })] : [],
        ...auto ? [updateAdjustedPlacement] : [],
        // @see https://floating-ui.com/docs/hide#strategy
        ...shouldHideOnAnchorHidden ? [
          hide({
            strategy: "referenceHidden",
            boundary: boundaryRef && boundaryRef.current ? boundaryRef.current : void 0
          })
        ] : [],
        // @see https://floating-ui.com/docs/hide#strategy
        ...shouldHideOnOverlayEscape ? [
          hide({
            strategy: "escaped",
            boundary: boundaryRef && boundaryRef.current ? boundaryRef.current : void 0
          })
        ] : []
      ]
    });
    useIsomorphicLayoutEffect(() => {
      refs.setReference(anchorRef.current);
    }, [refs, anchorRef]);
    React.useEffect(() => {
      if (!refs.reference.current && anchorRef.current) {
        refs.setReference(anchorRef.current);
      }
    }, [refs, anchorRef]);
    useIsomorphicLayoutEffect(() => {
      if (shouldReAttachAnchorRef) {
        refs.setReference(anchorRef.current);
        setShouldReAttachAnchorRef(false);
      }
    }, [refs, anchorRef, shouldReAttachAnchorRef]);
    useIsomorphicLayoutEffect(() => {
      const coords = middlewareData.arrow;
      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right"
      }[_floatingPlacement.split("-")[0]];
      if (coords && arrowRef && arrowRef.current) {
        Object.assign(
          arrowRef.current.style,
          {
            left: "",
            top: "",
            right: "",
            bottom: ""
          },
          {
            left: coords.x !== null ? `${coords.x}px` : "",
            top: coords.y !== null ? `${coords.y}px` : "",
            right: "",
            bottom: "",
            [staticSide]: "-6px"
          }
        );
      }
    }, [arrowRef, middlewareData.arrow, _floatingPlacement]);
    const isAnchorHidden = middlewareData.hide && middlewareData.hide.referenceHidden ? middlewareData.hide.referenceHidden : false;
    const isFloatingEscaped = middlewareData.hide && middlewareData.hide.escaped ? middlewareData.hide.escaped : false;
    React.useImperativeHandle(
      ref,
      () => ({
        update: () => {
          update();
          setShouldReAttachAnchorRef(true);
        }
      }),
      [update]
    );
    const cls = classNames("dig-Overlay", `dig-Overlay--${_floatingPlacement}`, className);
    return /* @__PURE__ */ React.createElement(Layer, { _elevateByZ: ELEVATED_BASE_INDEX, _preferAbsolute: true, isPortaled }, /* @__PURE__ */ React.createElement(
      "div",
      {
        className: cls,
        ref: refs.setFloating,
        style: {
          ...style,
          position: strategy,
          top: "0",
          left: "0",
          visibility: isFloatingEscaped || isAnchorHidden ? "hidden" : void 0,
          transform: `translate3d(${roundByDPR(Number(x))}px,${roundByDPR(Number(y))}px,0)`
        },
        ...rest
      },
      children
    ));
  }
);
Overlay.displayName = "Overlay";
export {
  Overlay
};
